import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "../../util/axiosInstance";
import { ChevronDown } from "lucide-react";
import {
  Download,
  Copy,
  FileSpreadsheet,
  FileText,
  FileType,
} from "lucide-react";
type Unit = { id: string; name: string };
type WeekOption = { value: number; label: string };
interface WeeklyReportsProps {
  units: Unit[];
  loadingUnitSearchData: boolean;
}

const getWeekNumber = (date = new Date()) => {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDays = (Number(date) - Number(startOfYear)) / 86400000;
  return Math.ceil((pastDays + startOfYear.getDay() + 1) / 7);
};

const getFirstLastDayOfWeek = (
  week: number,
  year = new Date().getFullYear()
) => {
  const firstDayOfYear = new Date(year, 0, 1);
  const firstWeekDay = new Date(
    firstDayOfYear.setDate(firstDayOfYear.getDate() + (week - 1) * 7)
  );
  const lastWeekDay = new Date(firstWeekDay);
  lastWeekDay.setDate(firstWeekDay.getDate() + 6);
  return `${firstWeekDay.toLocaleDateString()} - ${lastWeekDay.toLocaleDateString()}`;
};

export default function WeeklyReports({
  units,
  loadingUnitSearchData,
}: WeeklyReportsProps) {
  const [weekOptions, setWeekOptions] = useState<WeekOption[]>([]);
  const [selectedWeek, setSelectedWeek] = useState(getWeekNumber() - 1);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportData, setReportData] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);
  const unitMap: Record<string, string> =
    units?.reduce((acc, u) => {
      acc[u.id] = u.name;
      return acc;
    }, {} as Record<string, string>) || {};

  const filteredReportData = reportData
    ? Object.entries(reportData).filter(([unitId]) =>
        (unitMap[unitId] || unitId)
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : [];

  useEffect(() => {
    const currentWeek = getWeekNumber();
    const options = [];
    for (let i = 1; i <= 7; i++) {
      const weekNum = currentWeek - i;
      if (weekNum > 0) {
        options.push({
          value: weekNum,
          label: `${getFirstLastDayOfWeek(weekNum)} - Week ${weekNum}`,
        });
      }
    }
    setWeekOptions(options);
  }, []);

  const fetchWeeklyReport = async (week: number) => {
    if (!units || units.length === 0) return;
    try {
      setLoadingReport(true);
      setError("");
      const payload = {
        week,
        year: new Date().getFullYear(),
        units: units.map((u) => u.id),
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-week`,
        payload,
        {
          headers: { id: "anything", "Content-Type": "application/json" },
        }
      );
      setReportData(response.data.data.mydict);
    } catch (err) {
      console.error(err);
      setError("Error fetching report data");
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (units && units.length > 0) fetchWeeklyReport(selectedWeek);
  }, [selectedWeek, units]);

  useEffect(() => setPage(0), [searchQuery]);

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const copyToClipboard = () => {
    if (!filteredReportData.length) return;
    const headers =
      "Unit Name\tYear\tWeek\tStop(Hrs)\tRun(Hrs)\tCustomer(Hrs)\tPRC(Hrs)\tShutdowns";
    const textContent = filteredReportData
      .map(
        ([unitId, r]) =>
          `${unitMap[unitId] || unitId}\t${r.year}\t${r.week}\t${r.down}\t${
            r.up
          }\t${r.customer}\t${r.precision}\t${r.shutDowns}`
      )
      .join("\n");
    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => toast.success("Weekly report copied to clipboard"));
    setExportMenuOpen(false);
  };

  const exportToCSV = () => {
    if (!filteredReportData.length) return;
    const headers = [
      "Unit Name",
      "Year",
      "Week",
      "Stop (Hrs)",
      "Run (Hrs)",
      "Customer (Hrs)",
      "PRC (Hrs)",
      "Shutdowns",
    ];
    const rows = filteredReportData.map(([unitId, r]) => [
      unitMap[unitId] || unitId,
      r.year,
      r.week,
      r.down,
      r.up,
      r.customer,
      r.precision,
      r.shutDowns,
    ]);
    const csvString = [headers, ...rows]
      .map((r) => r.map((f) => `"${f}"`).join(","))
      .join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Weekly_Report.csv`;
    link.click();
    setExportMenuOpen(false);
    toast.success("Exported Weekly Report to CSV");
  };

  const exportToExcel = () => {
    if (!filteredReportData.length) return;
    const headers = [
      "Unit Name",
      "Year",
      "Week",
      "Stop (Hrs)",
      "Run (Hrs)",
      "Customer (Hrs)",
      "PRC (Hrs)",
      "Shutdowns",
    ];
    const rows = filteredReportData.map(([unitId, r]) => [
      unitMap[unitId] || unitId,
      r.year,
      r.week,
      r.down,
      r.up,
      r.customer,
      r.precision,
      r.shutDowns,
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Weekly Report");
    XLSX.writeFile(workbook, `Weekly_Report.xlsx`);
    setExportMenuOpen(false);
    toast.success("Exported Weekly Report to Excel");
  };

  const exportToPDF = () => {
    if (!filteredReportData.length) return;
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(12);
    doc.text("Weekly Units Report", 40, 30);
    const headers = [
      [
        "Unit Name",
        "Year",
        "Week",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
    ];
    const rows = filteredReportData.map(([unitId, r]) => [
      unitMap[unitId] || unitId,
      r.year,
      r.week,
      r.down,
      r.up,
      r.customer,
      r.precision,
      r.shutDowns,
    ]);
    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 3 },
    });
    doc.save("Weekly_Report.pdf");
    setExportMenuOpen(false);
    toast.success("Exported Weekly Report to PDF");
  };

  const sortedData = React.useMemo(() => {
    if (!filteredReportData) return [];
    return [...filteredReportData].sort(([aId, aR], [bId, bR]) => {
      let aValue, bValue;
      switch (orderBy) {
        case "Unit Name":
          aValue = unitMap[aId] || aId;
          bValue = unitMap[bId] || bId;
          break;
        case "Year":
          aValue = aR.year;
          bValue = bR.year;
          break;
        case "Week":
          aValue = aR.week;
          bValue = bR.week;
          break;
        case "Stop(Hrs)":
          aValue = aR.down;
          bValue = bR.down;
          break;
        case "Run(Hrs)":
          aValue = aR.up;
          bValue = bR.up;
          break;
        case "Customer(Hrs)":
          aValue = aR.customer;
          bValue = bR.customer;
          break;
        case "PRC(Hrs)":
          aValue = aR.precision;
          bValue = bR.precision;
          break;
        case "Shutdowns":
          aValue = aR.shutDowns;
          bValue = bR.shutDowns;
          break;
        default:
          return 0;
      }
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredReportData, order, orderBy]);

  const paginatedUnits = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="w-full flex flex-col rounded-lg bg-slate-900  p-5 ">
      {loadingUnitSearchData && (
        <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-20">
          <div className="animate-spin h-12 w-12 border-4 border-blue-400 border-t-transparent rounded-full" />
        </div>
      )}

      <div className="flex justify-between items-center flex-wrap gap-2 px-4 mt-2 mb-4">
        <h1 className="text-xl font-bold mt-5">Weekly Runtime Report</h1>
      </div>

      {loadingReport ? (
        <div className="w-full flex justify-center items-center min-h-[200px]">
          <div className="animate-spin h-12 w-12 border-4 border-blue-400 border-t-transparent rounded-full" />
        </div>
      ) : error ? (
        <div className="text-red-500 font-bold text-center py-4">{error}</div>
      ) : (
        <>
          <div className="p-3 bg-slate-900 flex flex-col gap-3 md:flex-row md:items-center md:gap-2">
            <input
              type="text"
              placeholder="Search Unit..."
              className="px-2 py-1 rounded bg-slate-800 text-white border border-slate-600 flex-1"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="grid grid-cols-4 gap-2 md:flex md:gap-2 md:w-auto">
              <button
                onClick={copyToClipboard}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <Copy size={12} className="inline-flex align-middle" /> Copy
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <FileText size={12} className="inline-flex align-middle" /> CSV
              </button>
              <button
                onClick={exportToExcel}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <FileSpreadsheet
                  size={12}
                  className="inline-flex align-middle"
                />
                Excel
              </button>
              <button
                onClick={exportToPDF}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <FileType size={12} className="inline-flex align-middle" /> PDF
              </button>
            </div>
            <select
              className="px-2 py-1 bg-gray-800 text-white rounded border border-gray-600"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(Number(e.target.value))}
            >
              {weekOptions.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
            {/* <span className="text-slate-400 text-xs">
            {filteredIndexes.length} / {tableData.time.length} rows
          </span> */}
          </div>
          <div className="overflow-auto max-h-[63vh] px-2 mt-5 rounded-lg">
            <div className="rounded-xl overflow-hidden border border-gray-700">
              <table className="w-full text-left  border-collapse">
                <thead className="bg-gray-700 text-white sticky top-0 hidden sm:table-header-group">
                  <tr className="border-b border-slate-700 bg-slate-800">
                    {[
                      "Unit Name",
                      "Year",
                      "Week",
                      "Stop(Hrs)",
                      "Run(Hrs)",
                      "Customer(Hrs)",
                      "PRC(Hrs)",
                      "Shutdowns",
                    ].map((col) => (
                      <th
                        key={col}
                        className="p-2 cursor-pointer select-none font-semibold text-center"
                        onClick={() => handleSort(col)}
                      >
                        <div className="flex items-center justify-center w-full">
                          <span className="flex items-center gap-1">{col}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedUnits.length ? (
                    paginatedUnits.map(([unitId, r], idx) => (
                      <React.Fragment key={unitId}>
                        <tr
                          className={`hidden sm:table-row ${
                            idx % 2 === 0 ? "bg-gray-800" : "bg-gray-700"
                          }`}
                        >
                          <td className="px-2 py-1">
                            {unitMap[unitId] || unitId}
                          </td>
                          <td className="px-2 py-1 text-center">{r.year}</td>
                          <td className="px-2 py-1 text-center">{r.week}</td>
                          <td className="px-2 py-1 text-center">
                            {r.down.toFixed(1)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {r.up.toFixed(1)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {r.customer.toFixed(1)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {r.precision.toFixed(1)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {r.shutDowns}
                          </td>
                        </tr>
                        <tr className="sm:hidden bg-gray-800 border-b border-gray-700">
                          <td colSpan={8} className="p-3">
                            {/* Header */}
                            <button
                              onClick={() =>
                                setExpandedUnit(
                                  expandedUnit === unitId ? null : unitId
                                )
                              }
                              className="w-full flex justify-between items-center text-left"
                            >
                              <span className="font-semibold text-[14px] text-white">
                                {unitMap[unitId] || unitId}
                              </span>
                              <ChevronDown
                                className={`w-4 h-4 transition-transform ${
                                  expandedUnit === unitId ? "rotate-180" : ""
                                }`}
                              />
                            </button>

                            {/* Expanded content */}
                            {expandedUnit === unitId && (
                              <div className="mt-3 space-y-2 text-sm text-gray-300">
                                <div className="flex justify-between">
                                  <span>Year</span>
                                  <span>{r.year}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Week</span>
                                  <span>{r.week}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Stop (Hrs)</span>
                                  <span>{r.down.toFixed(1)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Run (Hrs)</span>
                                  <span>{r.up.toFixed(1)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Customer (Hrs)</span>
                                  <span>{r.customer.toFixed(1)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>PRC (Hrs)</span>
                                  <span>{r.precision.toFixed(1)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Shutdowns</span>
                                  <span>{r.shutDowns}</span>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center py-4">
                        No units found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-end items-center py-2 px-3 text-white gap-2">
            <div className="w-32 text-right">Rows:</div>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              className="bg-gray-800 border border-gray-600 rounded px-1 py-1"
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Export Buttons Row */}
    </div>
  );
}
